import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../../components/notify";
import { IApiSubtitleSpeed, ISubtitleSpeedFormData } from "../../types";
import { INITIAL_SUBTITLE_SPEED } from "../configs";
import { getLanguageI18nKey } from "../utils";
import { subtitleSpeedSchema } from "../validations";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";

interface UseSubtitleSpeedSettingsProps {
    characterId: string;
}

const DEFAULT_SPEED = 1.0;

export const useSubtitleSpeedSettings = ({ characterId }: UseSubtitleSpeedSettingsProps) => {
    const { t } = useTranslation();
    const { kioskSetting, isLoadingKioskSetting, isUpdatingKioskSetting, updateSubtitleSpeed } = useKioskSettings({
        characterId,
    });

    const [selectedLanguage, setSelectedLanguage] = useState<string>("");

    const methods = useForm<ISubtitleSpeedFormData>({
        mode: "onChange",
        resolver: yupResolver(subtitleSpeedSchema()),
        defaultValues: { subtitle_speed: INITIAL_SUBTITLE_SPEED },
    });

    const { formState, reset, setValue, control } = methods;
    const isDirty = formState.isDirty;
    const isValid = formState.isValid;

    // 使用 useWatch 讓 subtitle_speed 變更時觸發 re-render
    const watchedSubtitleSpeed = useWatch({ control, name: "subtitle_speed" });

    // 從 LanguageSettings 已選擇的語言取得選項
    const languageOptions = useMemo(() => {
        const options = kioskSetting?.data?.native?.general?.languageOptions || [];
        return options.map((code) => ({
            value: code,
            label: t(getLanguageI18nKey(code)),
        }));
    }, [kioskSetting?.data?.native?.general?.languageOptions, t]);

    // 取得目前選中語言的速度值（透過 useWatch 實現即時響應）
    const currentSpeed = useMemo(() => {
        const speeds = watchedSubtitleSpeed || [];
        const found = speeds.find((item) => item.language === selectedLanguage);
        return found?.speed ?? DEFAULT_SPEED;
    }, [selectedLanguage, watchedSubtitleSpeed]);

    // 切換語言時的處理
    const handleLanguageChange = useCallback((language: string) => {
        setSelectedLanguage(language);
    }, []);

    // 更新速度值
    const handleSpeedChange = useCallback(
        (_event: Event, value: number | number[]) => {
            const speed = typeof value === "number" ? value : value[0];
            const currentData = watchedSubtitleSpeed || [];
            const index = currentData.findIndex((item) => item.language === selectedLanguage);

            if (index >= 0) {
                const updated = [...currentData];
                updated[index] = { ...updated[index], speed };
                setValue("subtitle_speed", updated, { shouldDirty: true });
            } else {
                setValue("subtitle_speed", [...currentData, { language: selectedLanguage, speed }], {
                    shouldDirty: true,
                });
            }
        },
        [selectedLanguage, watchedSubtitleSpeed, setValue]
    );

    const handleSubmit = useCallback(
        async (data: ISubtitleSpeedFormData) => {
            try {
                await updateSubtitleSpeed(data.subtitle_speed);
                reset(data);
                customToast.success(t("COMMON.SETTINGS_SAVED") || "設定已儲存");
            } catch {
                customToast.error(t("COMMON.SETTINGS_ERROR") || "儲存設定失敗");
            }
        },
        [updateSubtitleSpeed, reset, t]
    );

    const saveHandler = useCallback(async () => {
        await methods.handleSubmit(handleSubmit)();
    }, [methods, handleSubmit]);

    useTabGuard("subtitleSpeed", isDirty, saveHandler);

    const handleCancel = useCallback(() => {
        methods.reset();
    }, [methods]);

    const handleResetToDefaults = useCallback(() => {
        // 根據目前 LanguageSettings 的語言，每個都設為預設速度
        const options = kioskSetting?.data?.native?.general?.languageOptions || [];
        const defaultData: IApiSubtitleSpeed[] = options.map((lang) => ({
            language: lang,
            speed: DEFAULT_SPEED,
        }));
        setValue("subtitle_speed", defaultData, { shouldDirty: true });
    }, [kioskSetting?.data?.native?.general?.languageOptions, setValue]);

    // 從 API 載入資料
    useEffect(() => {
        if (!isLoadingKioskSetting && kioskSetting?.data?.native) {
            const apiSubtitleSpeed = kioskSetting.data.native.subtitle_speed || [];
            const languageOpts = kioskSetting.data.native.general?.languageOptions || [];

            // 為每個語言建立速度資料，若 API 沒有該語言則使用預設值
            const subtitleSpeedData: IApiSubtitleSpeed[] = languageOpts.map((lang) => {
                const existing = apiSubtitleSpeed.find((item) => item.language === lang);
                return { language: lang, speed: existing?.speed ?? DEFAULT_SPEED };
            });

            reset({ subtitle_speed: subtitleSpeedData });

            // 預設選中第一個語言
            if (!selectedLanguage && languageOpts.length > 0) {
                setSelectedLanguage(languageOpts[0]);
            }
        }
    }, [kioskSetting?.data?.native, isLoadingKioskSetting, reset, selectedLanguage]);

    return {
        methods,
        handleSubmit,
        handleCancel,
        handleResetToDefaults,
        handleLanguageChange,
        handleSpeedChange,
        languageOptions,
        selectedLanguage,
        currentSpeed,
        isLoadingKioskSetting,
        isUpdatingKioskSetting,
        isDirty,
        isValid,
    };
};
